/**
 * Responsibility: talk to the hub over HTTP, with types from the generated
 * schema rather than hand-written interfaces.
 *
 * Cloudflare Access gates the origin, so the browser arrives already
 * authenticated and this client carries no credential of its own. If Access
 * ever expires mid-session the hub answers with a redirect to a login page;
 * that is detected here and surfaced, because an HTML login page parsed as
 * JSON produces a baffling error a long way from its cause.
 */
import { freshToken, idToken } from '../auth/firebase';
import type { components } from './generated/schema';

// FastAPI emits separate Input and Output schemas for any model used in both
// a request body and a response, because their serialised shapes differ. The
// GUI reads Output and writes Input; conflating them is how a field that is
// computed on the way out gets sent on the way in.
export type Watch = components['schemas']['Watch-Output'];
export type WatchInput = components['schemas']['Watch-Input'];
export type StockEvent = components['schemas']['StockEvent-Output'];
export type NodeStatus = components['schemas']['NodeStatus'];
export type HealthReport = components['schemas']['HealthReport'];
export type PriceRule = components['schemas']['PriceRule-Output'];
export type NewsItem = components['schemas']['NewsItemOut'];
export type DropSignal = components['schemas']['DropSignalOut'];
export type NewsRegistryHealth = components['schemas']['RegistryHealthOut'];
export type SweepResult = components['schemas']['SweepOut'];
export type WatchCreate = components['schemas']['WatchCreate'];
export type WatchPatch = components['schemas']['WatchPatch'];
export type UrlPreview = components['schemas']['UrlPreview'];
export type Subscription = components['schemas']['SubscriptionOut'];
export type SubscriptionCreate = components['schemas']['SubscriptionCreate'];
export type SubscriptionPatch = components['schemas']['SubscriptionPatch'];
/** Carries a freshly minted credential. Shown once, never fetched again. */
export type SecretReveal = components['schemas']['SecretReveal'];

// Grading arbitrage. `GradingBoard` is deliberately the whole page in one
// response: adding a card reorders everything, and a client that merged one
// row into a sorted list would be a second copy of the ranking rules.
export type GradingBoard = components['schemas']['GradingBoard'];
export type Opportunity = components['schemas']['OpportunityOut'];
export type RejectedCandidate = components['schemas']['CandidateOut'];
export type GradingAssumptions = components['schemas']['AssumptionsOut'];
export type GradingSources = components['schemas']['SourceStatusOut'];
export type CardPrice = components['schemas']['PriceOut'];
export type Payoff = components['schemas']['PayoffOut'];
export type CardPopulation = components['schemas']['PopulationOut'];
export type CandidateCreate = components['schemas']['CandidateCreate'];
export type CandidatePatch = components['schemas']['CandidatePatch'];
export type PopulationInput = components['schemas']['PopulationIn'];

// Discovery boards. A board is derived rather than stored, so every mutation
// hands back the refilled board and the client never merges rows itself.
export type DiscoveryBoard = components['schemas']['BoardOut'];
export type DiscoveredCard = components['schemas']['DiscoveredCardOut'];
export type DiscoveryArchive = components['schemas']['ArchiveOut'];
export type ArchivedCard = components['schemas']['ArchivedCardOut'];
export type CardEra = components['schemas']['CardEra'];
export type CardLanguage = components['schemas']['CardLanguage'];
export type CardDetail = components['schemas']['CardDetailOut'];
export type SoldPrices = components['schemas']['SoldOut'];
export type Listing = components['schemas']['ListingOut'];

// The broad card market: sports, signed and trading cards alike, priced
// from completed sales rather than from what a seller is asking.
export type MarketSearch = components['schemas']['MarketSearchOut'];
export type MarketResult = components['schemas']['MarketResultOut'];
export type MarketCardDetail = components['schemas']['MarketCardOut'];
export type MarketMode = components['schemas']['MarketMode'];
export type MarketCategory = components['schemas']['MarketCategory'];
export type GradeQuote = components['schemas']['GradeQuoteOut'];
export type SoldSale = components['schemas']['SoldSaleOut'];
export type Collection = components['schemas']['CollectionOut'];
export type CollectionItem = components['schemas']['CollectionItemOut'];
/** A named sub-collection: what it is for, and what it is aiming at. */
export type CollectionTag = components['schemas']['TagOut'];
export type NewTag = components['schemas']['TagIn'];
export type TagEdit = components['schemas']['TagEditIn'];
/** One set a person owns cards from, with completion where it is known. */
export type SetSummary = components['schemas']['SetSummaryOut'];
/** A whole set, with each card marked owned or missing. */
export type SetDetail = components['schemas']['SetDetailOut'];
export type CollectionSummary = components['schemas']['CollectionSummaryOut'];
export type ValuePoint = components['schemas']['ValuePointOut'];
export type Saved = components['schemas']['SavedOut'];
export type LikedCard = components['schemas']['LikedCardOut'];
export type LikedBoard = components['schemas']['LikedBoardOut'];

/** A query plus what it found, which is what makes a saved row readable. */
export type SaveSearchBody = MarketQuery & {
  label?: string;
  preview_images?: string[];
  result_count?: number;
};
export type SavedSearch = components['schemas']['SavedSearchOut'];
export type ArchiveIntent = components['schemas']['ArchiveIntent'];
export type SalePoint = components['schemas']['SalePointOut'];
export type OutlierScan = components['schemas']['OutlierScanOut'];
export type Outlier = components['schemas']['OutlierOut'];
export type StandingOutliers = components['schemas']['StandingOutliersOut'];
export type CatalogueInfo = components['schemas']['CatalogueOut'];
export type CatalogueBoard = components['schemas']['CatalogueBoardOut'];
export type CatalogueRow = components['schemas']['CatalogueRowOut'];
export type Account = components['schemas']['AccountOut'];
export type StorageUsage = components['schemas']['UsageOut'];
export type AdminSecret = components['schemas']['SecretOut'];
export type ScanResult = components['schemas']['ScanOut'];
/** The scanner's text readers, and whether each is alive. */
export type ScanEngines = components['schemas']['EnginesOut'];
export type ScanEngine = components['schemas']['EngineStatusOut'];
export type UserSettings = components['schemas']['SettingsOut'];
export type UserSettingsInput = components['schemas']['SettingsIn'];

export class SignedOutError extends Error {
  constructor(message = 'Your session has ended. Sign in again to continue.') {
    super(message);
    this.name = 'SignedOutError';
  }
}

/**
 * The hub wants the user to prove who they are *again*, not a fresher token.
 *
 * These are different failures behind the same status code, and conflating
 * them is why saving a credential never worked. Refreshing an ID token does
 * not move its `auth_time` - only actually re-authenticating does - so the
 * generic retry below could never clear this, and the second 401 was reported
 * as a dead session. The admin routes say which case it is in
 * `WWW-Authenticate`, so that is what decides.
 */
export class ReauthRequiredError extends Error {
  constructor(message = 'Confirm it is you before changing credentials.') {
    super(message);
    this.name = 'ReauthRequiredError';
  }
}

export class HubError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HubError';
    this.status = status;
  }
}

/**
 * The base the API lives at.
 *
 * Empty in development and when the hub serves the bundle itself, because a
 * relative path is same-origin and needs no configuration. Set once the GUI
 * moves to Firebase Hosting, where the API is on another host entirely.
 */
const API_BASE = import.meta.env.VITE_HUB_URL ?? '';

async function send(path: string, init: RequestInit | undefined, token: string | null) {
  // **A JSON body needs to say so, and four calls forgot.** `fetch` with a
  // string body and no `Content-Type` sends `text/plain`, which FastAPI
  // will not parse into a model - so the request comes back 422 with a
  // validation error, and the feature simply does not work. It cost
  // sub-collections entirely: creating, renaming, and adding either a like
  // or a holding to one were the four calls that set the header nowhere.
  //
  // Set here rather than at each call site so the next one cannot forget.
  // Only for a string body: a `FormData` body must be left alone, because
  // the browser writes its own multipart boundary into that header and
  // overriding it makes the upload unparseable at the other end.
  const jsonBody = typeof init?.body === 'string';
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(jsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
    redirect: 'follow',
  });
}

/**
 * What the hub said went wrong, as something a person can read.
 *
 * **`detail` is not always a string.** A hand-written `HTTPException`
 * carries one, but a validation failure carries FastAPI's own array of
 * `{loc, msg, type}` objects - and rendering that straight into an alert
 * produces `[object Object]`, which tells the reader nothing at all and
 * tells whoever is debugging it even less.
 */
function readDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail !== '') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        const item = entry as { msg?: unknown; loc?: unknown };
        const where = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
        const what = typeof item.msg === 'string' ? item.msg : '';
        if (what === '') return '';
        return where === '' ? what : `${where}: ${what}`;
      })
      .filter((part) => part !== '');
    if (parts.length > 0) return parts.join('; ');
  }
  return fallback;
}

/** Whether a 401 is the hub asking for a fresh sign-in rather than any token. */
function needsReauthentication(response: Response): boolean {
  return (response.headers.get('www-authenticate') ?? '').includes('reauth_required');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response = await send(path, init, await idToken());

  if (response.status === 401) {
    // Asking for a fresh sign-in is not the same as an expired session, and
    // a token refresh cannot satisfy it: `auth_time` only moves when the
    // user actually re-authenticates. Retrying here would spend the one
    // retry on something that cannot work and then report a dead session.
    if (needsReauthentication(response)) {
      const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
      const said = readDetail(body.detail, '');
      throw new ReauthRequiredError(said === '' ? undefined : said);
    }

    // One forced refresh, then give up. Firebase renews a token close to
    // expiry on its own, so a 401 usually means the session is genuinely
    // over - but a clock skew or a token that expired mid-flight is worth
    // exactly one retry before throwing the user back to the sign-in screen.
    const fresh = await freshToken();
    if (fresh !== null) {
      response = await send(path, init, fresh);
    }
    if (response.status === 401) {
      if (needsReauthentication(response)) {
        throw new ReauthRequiredError();
      }
      throw new SignedOutError();
    }
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // The hub only ever answers JSON. Anything else means something in front
    // of it intercepted the request.
    throw new SignedOutError('The hub did not answer with JSON. Reload and try again.');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
    throw new HubError(response.status, readDetail(body.detail, response.statusText));
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const hubApi = {
  /** Who is signed in, as the hub sees them. Provisions on first call. */
  account: () => request<Account>('/api/account'),
  /** What this person assumes when a payoff is computed. */
  settings: () => request<UserSettings>('/api/account/settings'),
  saveSettings: (body: UserSettingsInput) =>
    request<UserSettings>('/api/account/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  /** Everything owned, as JSON. Never includes a notification credential. */
  /**
   * How much room this account has left.
   *
   * `refresh` forces a fresh count rather than the cached one, which is what
   * the button after a deletion needs: without it somebody deletes fifty
   * holdings and the figure does not move for half a minute.
   */
  usage: (refresh = false) =>
    request<StorageUsage>(`/api/account/usage${refresh ? '?refresh=true' : ''}`),
  /**
   * The hub's own credentials, masked.
   *
   * Masked is all the browser ever receives by default. Revealing one is a
   * separate, explicit request for a single named secret, and the hub refuses
   * it unless the session was re-authenticated in the last few minutes.
   */
  /**
   * Measure one photograph of one face.
   *
   * Multipart rather than JSON: a base64 image is a third larger, and on a
   * phone connection that third is seconds. `withOverlay` asks for the
   * working back - the detected boundary and the magnified corners - which is
   * what the verify page renders.
   */
  scan: async (file: File, face: 'front' | 'back', withOverlay: boolean) => {
    const body = new FormData();
    body.append('image', file);
    body.append('face', face);
    body.append('include_overlay', String(withOverlay));
    return request<ScanResult>('/api/scan', { method: 'POST', body });
  },
  /**
   * Identify the card in a photograph.
   *
   * Separate from `scan`, which measures a card's condition. Two
   * different questions about the same picture, and the answers have
   * nothing in common.
   */
  identifyCard: (file: File, game = '', language = '') => {
    const body = new FormData();
    body.append('image', file);
    // Omitted rather than sent empty when nobody said: the hub treats
    // both the same, and not sending it keeps "nothing was said" out of
    // the request log as a value.
    if (game) body.append('game', game);
    if (language) body.append('language', language);
    return request<IdentifyResult>('/api/identify', { method: 'POST', body });
  },
  /**
   * Whether each text reader the scanner uses is answering.
   *
   * Asked on demand rather than polled: a free OCR tier is rationed and
   * a health check spends the same quota a scan does.
   */
  scanEngines: () => request<ScanEngines>('/api/scan/engines'),
  adminSecrets: () => request<AdminSecret[]>('/api/admin/secrets'),
  revealSecret: (key: string) =>
    request<{ key: string; value: string }>(`/api/admin/secrets/${key}/reveal`, {
      method: 'POST',
    }),
  setSecret: (key: string, value: string) =>
    request<AdminSecret>(`/api/admin/secrets/${key}`, {
      method: 'PUT',
      // Without this the browser labels a JSON string `text/plain` and
      // FastAPI rejects the body as unprocessable. Every other write in this
      // file sets it; this one did not, which is why saving a credential
      // failed however valid the value was.
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    }),
  discordChannels: () => request<DiscordState>('/api/admin/discord'),
  dataHub: () => request<DataHubState>('/api/admin/datahub'),
  indexStatus: () => request<IndexStatus>('/api/admin/index'),
  calibrationProgress: () => request<CalibrationProgress>('/api/admin/calibration/progress'),
  setDiscordChannels: (channels: DiscordChannelInput[]) =>
    request<DiscordState>('/api/admin/discord', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channels }),
    }),

  deleteSecret: (key: string) =>
    request<void>(`/api/admin/secrets/${key}`, { method: 'DELETE' }),
  exportAccount: () => request<Record<string, unknown>>('/api/account/export'),
  deleteAccount: () => request<void>('/api/account', { method: 'DELETE' }),

  health: () => request<HealthReport>('/healthz'),
  listWatches: () => request<Watch[]>('/api/watches'),
  createWatch: (payload: WatchCreate) =>
    request<Watch>('/api/watches', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  previewWatchUrl: (url: string) =>
    request<UrlPreview>('/api/watches/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // product_name is required by the shared schema but unused by preview.
      body: JSON.stringify({ url, product_name: 'preview' }),
    }),
  patchWatch: (watchId: string, patch: WatchPatch) =>
    request<Watch>(`/api/watches/${watchId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  upsertWatch: (watch: WatchInput) =>
    request<Watch>(`/api/watches/${watch.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(watch),
    }),
  deleteWatch: (watchId: string) =>
    request<void>(`/api/watches/${watchId}`, { method: 'DELETE' }),
  listEvents: (limit = 100) => request<StockEvent[]>(`/api/events?limit=${limit}`),
  listNodes: () => request<NodeStatus[]>('/api/nodes'),
  listNews: (limit = 50) => request<NewsItem[]>(`/api/news/items?limit=${limit}`),
  listDropSignals: () => request<DropSignal[]>('/api/news/signals'),
  newsSources: () => request<NewsRegistryHealth>('/api/news/sources'),
  sweepNews: () => request<SweepResult>('/api/news/sweep', { method: 'POST' }),

  listSubscriptions: () => request<Subscription[]>('/api/notify/subscriptions'),
  createSubscription: (payload: SubscriptionCreate) =>
    request<SecretReveal>('/api/notify/subscriptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  rotateSubscription: (id: string) =>
    request<SecretReveal>(`/api/notify/subscriptions/${id}/rotate`, { method: 'POST' }),
  patchSubscription: (id: string, patch: SubscriptionPatch) =>
    request<Subscription>(`/api/notify/subscriptions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  testSubscription: (id: string) =>
    request<Subscription>(`/api/notify/subscriptions/${id}/test`, { method: 'POST' }),
  deleteSubscription: (id: string) =>
    request<void>(`/api/notify/subscriptions/${id}`, { method: 'DELETE' }),
  listNotifyLists: () => request<string[]>('/api/notify/lists'),

  // Expensive: a catalogue page and two eBay searches per card. Asked
  // for explicitly, never on a page load.
  outlierScan: (query: string) =>
    request<OutlierScan>(`/api/market/outliers?q=${encodeURIComponent(query)}`),

  // Sports and every trading card game, screened from the price
  // catalogue rather than from a Pokemon population report.
  catalogues: () => request<CatalogueInfo[]>('/api/catalogue/catalogues'),
  catalogueBoard: (key: string, keywords: string, maxRaw: string) => {
    const params = new URLSearchParams();
    if (keywords.trim()) params.set('keywords', keywords.trim());
    if (maxRaw.trim()) params.set('max_raw', maxRaw.trim());
    const query = params.toString();
    return request<CatalogueBoard>(`/api/catalogue/board/${key}${query ? `?${query}` : ''}`);
  },
  buildCatalogue: (key: string) =>
    request<CatalogueBoard>(`/api/catalogue/board/${key}/build`, { method: 'POST' }),

  // What the hourly sweep of your own cards last found. A read, never
  // a scan: opening a page should not start fifty eBay searches.
  standingOutliers: () => request<StandingOutliers>('/api/market/outliers/standing'),
  sweepOutliers: () =>
    request<StandingOutliers>('/api/market/outliers/sweep', { method: 'POST' }),

  gradingBoard: () => request<GradingBoard>('/api/grading'),
  addGradingCandidate: (payload: CandidateCreate) =>
    request<GradingBoard>('/api/grading', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  // Promoting something already being watched. The hub looks the card up
  // from its own store, so only the key travels.
  addGradingFromWatched: (discoveryKey: string) =>
    request<GradingBoard>('/api/grading/from-watched', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discovery_key: discoveryKey }),
    }),
  addGradingFromLike: (productKey: string) =>
    request<GradingBoard>('/api/grading/from-like', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_key: productKey }),
    }),
  refreshGrading: () => request<GradingBoard>('/api/grading/refresh', { method: 'POST' }),
  refreshGradingCandidate: (id: string) =>
    request<GradingBoard>(`/api/grading/${id}/refresh`, { method: 'POST' }),
  setGradingPopulation: (id: string, payload: PopulationInput) =>
    request<GradingBoard>(`/api/grading/${id}/population`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  // One PATCH for both mutable fields. An omitted field is left alone, and
  // an empty-string spec ID clears it - which is why this sends the patch
  // verbatim rather than filtering falsy values out of it.
  patchGradingCandidate: (id: string, patch: CandidatePatch) =>
    request<GradingBoard>(`/api/grading/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),
  deleteGradingCandidate: (id: string) =>
    request<void>(`/api/grading/${id}`, { method: 'DELETE' }),

  discoveryBoard: (era: CardEra, floor: number, language: CardLanguage) =>
    request<DiscoveryBoard>(
      `/api/discovery?era=${era}&floor=${floor}&language=${language}`,
    ),
  // One request per card, so only called when a detail dialog opens.
  discoveryCard: (discoveryKey: string) =>
    request<CardDetail>(`/api/discovery/card/${encodeURIComponent(discoveryKey)}`),
  discoveryArchive: (era: CardEra, intent: ArchiveIntent = 'BINNED') =>
    request<DiscoveryArchive>(`/api/discovery/archive?era=${era}&intent=${intent}`),
  archiveDiscovered: (
    era: CardEra,
    floor: number,
    language: CardLanguage,
    discoveryKey: string,
    intent: ArchiveIntent = 'BINNED',
  ) =>
    request<DiscoveryBoard>(
      `/api/discovery/archive?era=${era}&floor=${floor}&language=${language}`,
      {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discovery_key: discoveryKey, intent }),
      },
    ),
  restoreDiscovered: (era: CardEra, discoveryKey: string) =>
    request<DiscoveryArchive>(
      `/api/discovery/archive/${encodeURIComponent(discoveryKey)}?era=${era}`,
      { method: 'DELETE' },
    ),
  refreshDiscovery: (era: CardEra, floor: number, language: CardLanguage) =>
    request<DiscoveryBoard>(
      `/api/discovery/refresh?era=${era}&floor=${floor}&language=${language}`,
      { method: 'POST' },
    ),

  // A search reads a product page per result, so it is the slowest call in
  // the system and is only ever made when the operator asks for it.
  marketSearch: (params: MarketQuery) => {
    const query = new URLSearchParams({ q: params.q, mode: params.mode });
    if (params.category) query.set('category', params.category);
    if (params.minRaw) query.set('min_raw', params.minRaw);
    if (params.maxRaw) query.set('max_raw', params.maxRaw);
    if (params.minValue) query.set('min_value', params.minValue);
    if (params.minYear) query.set('min_year', params.minYear);
    if (params.maxYear) query.set('max_year', params.maxYear);
    if (params.setContains) query.set('set_contains', params.setContains);
    if (params.gradedOnly) query.set('graded_only', 'true');
    // How far into the same candidate list to start. The references all
    // came from one page, so a later window only costs product reads.
    if (params.offset) query.set('offset', String(params.offset));
    return request<MarketSearch>(`/api/market/search?${query.toString()}`);
  },
  marketCard: (key: string) => request<MarketCardDetail>(`/api/market/card/${key}`),

  collection: () => request<Collection>('/api/collection'),
  addToCollection: (body: AddHolding) =>
    request<CollectionItem>('/api/collection', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  editHolding: (itemId: string, changes: EditHolding) =>
    request<CollectionItem>(`/api/collection/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    }),
  removeFromCollection: (itemId: string) =>
    request<void>(`/api/collection/${itemId}`, { method: 'DELETE' }),
  revalueCollection: () => request<Collection>('/api/collection/revalue', { method: 'POST' }),

  collectionSets: () => request<SetSummary[]>('/api/collection/sets'),
  setDetail: (label: string) =>
    request<SetDetail>(`/api/collection/sets/${encodeURIComponent(label)}`),

  tags: () => request<CollectionTag[]>('/api/collection/tags'),
  addTag: (body: NewTag) =>
    request<CollectionTag>('/api/collection/tags', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  editTag: (tagId: string, changes: TagEdit) =>
    request<CollectionTag>(`/api/collection/tags/${tagId}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),
  removeTag: (tagId: string) =>
    request<void>(`/api/collection/tags/${tagId}`, { method: 'DELETE' }),
  /** The product key is a path parameter and carries slashes, so it is
   *  not encoded - the route is declared to match them. */
  setLikeTags: (productKey: string, tags: string[]) =>
    request<LikedCard>(`/api/saved/likes/${productKey}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ tags }),
    }),

  /** Replaces the card's whole set of sub-collections, never merges. */
  setHoldingTags: (itemId: string, tags: string[]) =>
    request<CollectionItem>(`/api/collection/${itemId}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ tags }),
    }),

  saved: () => request<Saved>('/api/saved'),
  likedCards: () => request<LikedBoard>('/api/saved/likes/cards'),
  watchedCards: () => request<LikedBoard>('/api/saved/watching/cards'),
  likeCard: (productKey: string) =>
    request<LikedCard>('/api/saved/likes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_key: productKey }),
    }),
  // A discovery row has no product key; the hub resolves the match.
  likeDiscovered: (discoveryKey: string) =>
    request<LikedCard>('/api/saved/likes/from-discovery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discovery_key: discoveryKey }),
    }),
  unlikeCard: (productKey: string) =>
    request<void>(`/api/saved/likes/${productKey}`, { method: 'DELETE' }),
  /**
   * Track a card.
   *
   * The same document as a like, in a different list. Liking is "show me
   * this again"; watching is "tell me when this moves".
   */
  watchCard: (productKey: string) =>
    request<LikedCard>('/api/saved/watching', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_key: productKey }),
    }),
  unwatchCard: (productKey: string) =>
    request<void>(`/api/saved/watching/${productKey}`, { method: 'DELETE' }),
  saveSearch: (body: SaveSearchBody) =>
    request<SavedSearch>('/api/saved/searches', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        label: body.label ?? '',
        query: body.q,
        mode: body.mode,
        category: body.category || null,
        min_raw: body.minRaw || null,
        max_raw: body.maxRaw || null,
        min_value: body.minValue || null,
        // A snapshot of what it found, so the saved list is recognisable
        // without re-running every row.
        preview_images: body.preview_images ?? [],
        result_count: body.result_count ?? null,
      }),
    }),
  markSearchRun: (searchId: string) =>
    request<void>(`/api/saved/searches/${searchId}/ran`, { method: 'POST' }),
  forgetSearch: (searchId: string) =>
    request<void>(`/api/saved/searches/${searchId}`, { method: 'DELETE' }),
};

/** What the search box and its filter bar can send. */
export type DiscordState = components['schemas']['DiscordStateOut'];

/** What the research corpus holds, against what exists. One read, no fetching. */
export type DataHubState = components['schemas']['DataHubOut'];
export type DataHubSet = components['schemas']['SetStatusOut'];
export type DataHubCategory = components['schemas']['CategoryCountOut'];
export type DataHubGrade = components['schemas']['GradeCountOut'];

/** How far the catalogue crawl and the scan harvest have got. */
export type IndexStatus = components['schemas']['IndexStatusOut'];

/**
 * How far each grading metric is from working.
 *
 * **Declared here rather than taken from `components['schemas']`**, which
 * is generated from `openapi.json` and regenerated by another hand. These
 * should become `CalibrationProgressOut` and friends the next time that
 * file is rebuilt; until then a local shape avoids editing a generated
 * file from two directions.
 */
export type CalibrationAccuracy = {
  scored: number;
  within: number;
  mean_error: number;
  bias: number;
  baseline_error: number;
  spread: number;
  correlation: number;
  beats_guessing: boolean;
  sound: boolean;
};

export type CalibrationMetric = {
  key: string;
  name: string;
  outputs: string;
  truth: string;
  state: string;
  completeness: number;
  steps: Record<string, boolean>;
  accuracy: CalibrationAccuracy | null;
  ground_truth: number;
  underway: string;
  blocked_by: string;
  notes: string[];
};

export type CalibrationProgress = {
  metrics: CalibrationMetric[];
  walk: number;
  cards_measured: number;
  within_points: number;
  good_enough: number;
};

/** What the scanner made of a photograph: a shortlist, never a verdict. */
export type IdentifyResult = components['schemas']['IdentifyResult'];
export type IdentifiedCard = components['schemas']['IdentifiedCard'];
export type CataloguePace = components['schemas']['CataloguePace'];
export type DiscordChannel = components['schemas']['DiscordChannelOut'];
export type DiscordChannelInput = components['schemas']['DiscordChannelIn'];

export type MarketQuery = {
  q: string;
  mode: MarketMode;
  category?: MarketCategory | '';
  minRaw?: string;
  maxRaw?: string;
  minValue?: string;
  /** Inclusive year bounds, read from the set's name. */
  minYear?: string;
  maxYear?: string;
  /** A phrase the set name must contain. */
  setContains?: string;
  /** Only cards the catalogue holds a graded price for. */
  gradedOnly?: boolean;
  /**
   * Where in the candidate list this window starts.
   *
   * Part of the query rather than a separate call, so the cache keys on
   * it and going back to an earlier page is free.
   */
  offset?: number;
};

/**
 * A partial edit to a holding.
 *
 * `cleared` names the fields to blank, so the server can tell "empty this"
 * from "the form did not touch it" - a partial edit that wiped whatever it
 * omitted would lose data silently.
 */
export type EditHolding = {
  grade_label?: string | null;
  grading_company?: string | null;
  cert_number?: string | null;
  acquired_on?: string | null;
  quantity?: number | null;
  cost_basis?: string | null;
  notes?: string | null;
  cleared?: string[];
};

export type AddHolding = {
  product_key: string;
  grade_label?: string;
  /** Null for a raw copy. Drives the collection tile's border colour. */
  grading_company?: string | null;
  quantity?: number;
  cost_basis?: string | null;
  notes?: string;
};
